/** @format */

import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { map as lmap } from 'lodash-es';
import { ModelMapper } from 'model-mapper';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { GroupTreeElement, Intervention, InterventionStatus } from 'sesio-lib';
import { environment } from '../../environments/environment';
import { buildHeaderSearchFilter, IHeaderSearch } from '../_classes/header-search';
import { IDatatableOptions, IDatatableRecords } from '../_components/datagrid/datatable.class';

export interface IInterventionFilter extends IHeaderSearch {
  statuses?: InterventionStatus[];
  realEstateStructureIds?: string[];
  inspectionIds?: string[];
  year?: number;
}

@Injectable({
  providedIn: 'root',
})
export class InterventionService {
  constructor(protected http: HttpClient) {}

  public get(id: string): Observable<Intervention> {
    return this.http
      .get<any[]>(`${environment.apiUrl}/interventions/${id}`)
      .pipe(map(data => new ModelMapper(Intervention).map(data)));
  }

  public list(
    {
      statuses,
      organizationalUnitId,
      realEstateStructureIds,
      inspectionIds,
      equipmentIds,
      contractorId,
    }: {
      statuses?: InterventionStatus[];
      organizationalUnitId?: string;
      realEstateStructureIds?: string[];
      inspectionIds?: string[];
      equipmentIds?: string[];
      contractorId?: string;
    },
    skip: number = 0,
    limit?: number
  ): Observable<Intervention[]> {
    const params: any = { skip };
    if (limit) params.limit = limit;
    if (statuses?.length) {
      params.statuses = statuses;
    }
    if (organizationalUnitId) {
      params.organizationalUnitId = organizationalUnitId;
    }
    if (realEstateStructureIds?.length) {
      params.realEstateStructureIds = realEstateStructureIds;
    }
    if (inspectionIds?.length) {
      params.inspectionIds = inspectionIds;
    }
    if (equipmentIds?.length) {
      params.equipmentIds = equipmentIds;
    }
    if (contractorId) {
      params.contractorId = contractorId;
    }
    return this.http
      .get<any[]>(`${environment.apiUrl}/interventions`, { params })
      .pipe(map(data => lmap(data, d => new ModelMapper(Intervention).map(d))));
  }

  public getStatus(search?: IInterventionFilter): Observable<{ name: string; value: number }[]> {
    return this.http.post<any>(`${environment.apiUrl}/interventions/get-status`, this.buildFilter(search));
  }

  public create(data: any): Observable<Intervention> {
    return this.http
      .post<any>(`${environment.apiUrl}/interventions`, data)
      .pipe(map(data => new ModelMapper(Intervention).map(data)));
  }

  public update(id: string, data: any): Observable<boolean> {
    return this.http.patch<boolean>(`${environment.apiUrl}/interventions/${id}`, data);
  }

  public cancel(id: string): Observable<boolean> {
    return this.http.patch<boolean>(`${environment.apiUrl}/interventions/${id}/cancel`, {});
  }

  public getMapInfo(search: IHeaderSearch): Observable<
    {
      _id: string;
      coordinates: [number, number];
      status: string;
      state: string;
    }[]
  > {
    return this.http.post<any[]>(`${environment.apiUrl}/interventions/get-map-info`, buildHeaderSearchFilter(search));
  }

  public getTree(search: IHeaderSearch): Observable<GroupTreeElement[]> {
    return this.http
      .post<any[]>(`${environment.apiUrl}/interventions/get-tree`, buildHeaderSearchFilter(search))
      .pipe(map(data => data.map(d => new ModelMapper(GroupTreeElement).map(d))));
  }

  public datatable(
    query: IDatatableOptions,
    search?: IInterventionFilter,
    box?: number[][]
  ): Observable<IDatatableRecords<any>> {
    const params: any = {};
    if (box?.length) params.box = box;
    return this.http.post<any>(
      `${environment.apiUrl}/interventions/datatable`,
      { query, filter: this.buildFilter(search) },
      { params }
    );
  }

  public interventionReportDatatable(
    query: IDatatableOptions,
    search?: IInterventionFilter,
    box?: number[][]
  ): Observable<IDatatableRecords<any>> {
    const params: any = {};
    if (box?.length) params.box = box;
    return this.http.post<any>(
      `${environment.apiUrl}/interventions/intervention-report-datatable`,
      { query, filter: this.buildFilter(search) },
      { params }
    );
  }

  private buildFilter(search: IInterventionFilter): any {
    const filter = buildHeaderSearchFilter(search);
    if (search?.statuses?.length) {
      filter.statuses = search.statuses;
    }
    if (search?.realEstateStructureIds?.length) {
      filter.realEstateStructureIds = search.realEstateStructureIds;
    }
    if (search?.inspectionIds?.length) {
      filter.inspectionIds = search.inspectionIds;
    }
    if (search?.year) filter.year = search.year;
    return filter;
  }
}
